import useSWR from "swr";
import {
  SOCIAL_TOPIC_AUTHORS_LIMIT_MAX,
  SocialTopicAuthorsResponse,
  SocialTopicAuthorsSort,
  SocialTopicFilters,
} from "@digital-twins/shared-types";
import { useSocialListeningApi } from "./SocialListeningApiContext";
import { socialTopicFiltersCacheKey } from "./useSocialTopicFilters";
import { SOCIAL_UNIVERSE_COUNTS_SWR_KEY } from "./useSocialUniverse";

export interface UseSocialTopicAuthorsOptions {
  /** Null makes no request. */
  token: string | null;
  filters: SocialTopicFilters;
  sort: SocialTopicAuthorsSort;
  limit: number;
  /** Keeps the authors whose handle or name contains it; absent for none. */
  search?: string;
  refreshKey: string | number | null;
}

export interface UseSocialTopicAuthorsResult {
  authors: SocialTopicAuthorsResponse | null;
  isLoading: boolean;
  error: Error | null;
}

function clampLimit(limit: number): number {
  if (!Number.isFinite(limit)) {
    return SOCIAL_TOPIC_AUTHORS_LIMIT_MAX;
  }
  return Math.min(SOCIAL_TOPIC_AUTHORS_LIMIT_MAX, Math.max(1, Math.round(limit)));
}

/**
 * In the counts key family (`SOCIAL_UNIVERSE_COUNTS_SWR_KEY`, token, query,
 * refresh key), so whatever refetches a Topic's counts (a new `refreshKey`,
 * or a hide's `isSocialUniverseCountsKey` mutate) refetches its authors too.
 */
export function socialTopicAuthorsSwrKey(
  token: string,
  filters: SocialTopicFilters,
  sort: SocialTopicAuthorsSort,
  limit: number,
  refreshKey: string | number | null,
  search = "",
): [string, string, string, string | number | null] {
  return [
    SOCIAL_UNIVERSE_COUNTS_SWR_KEY,
    token,
    `authors:${JSON.stringify([socialTopicFiltersCacheKey(filters), sort, clampLimit(limit), ...(search ? [search] : [])])}`,
    refreshKey,
  ];
}

/**
 * `GET .../universes/:token/authors` under `filters` (top voices), keyed with
 * the counts so a refresh refetches both. Keeps the previous authors on
 * screen while a new filter loads.
 */
export function useSocialTopicAuthors(
  options: UseSocialTopicAuthorsOptions,
): UseSocialTopicAuthorsResult {
  const { token, filters, sort, limit, refreshKey, search = "" } = options;
  const socialListeningApi = useSocialListeningApi();

  const { data, error, isLoading } = useSWR<SocialTopicAuthorsResponse, Error>(
    token ? socialTopicAuthorsSwrKey(token, filters, sort, limit, refreshKey, search) : null,
    () =>
      socialListeningApi.getUniverseAuthors(token as string, {
        filters,
        sort,
        limit: clampLimit(limit),
        ...(search ? { authorSearch: search } : {}),
      }),
    { revalidateOnFocus: false, keepPreviousData: true, shouldRetryOnError: false },
  );

  return {
    authors: data ?? null,
    isLoading: Boolean(token) && isLoading,
    error: error ?? null,
  };
}
